import React, { useEffect, useState } from "react";
import {
  Plan,
  createPlan,
  createStep,
  getThreadPlans,
  updatePlan,
} from "../api/client";
import PlanBuilder from "./PlanBuilder";

type ThreadWorkspaceProps = {
  threadId: number;
  onOpenProject: (plan: Plan) => void;
};

export default function ThreadWorkspace({
  threadId,
  onOpenProject,
}: ThreadWorkspaceProps) {

  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  
  const [planTitle, setPlanTitle] = useState("");
  const [planDescription, setPlanDescription] = useState("");
  const [creationMode, setCreationMode] = useState<"guided" | "manual">("guided");

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
 
  useEffect(() => {
    let cancelled = false;

    async function loadPlans() {
      setLoadingPlans(true);
      setError(null);

      try {
        const data = await getThreadPlans(threadId);
        if (cancelled) return;

        setPlans(data);
        setSelectedPlanId((current) => {
          if (current && data.some((plan) => plan.id === current)) {
            return current;
          }
          return data.length > 0 ? data[0].id : null;
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load plans");
        }
      } finally {
        if (!cancelled) {
          setLoadingPlans(false);
        }
      }
    }

    loadPlans();

    return () => {
      cancelled = true;
    };
  }, [threadId]);

   useEffect(() => {
  if (!successMessage) return;

  const timer = setTimeout(() => {
    setSuccessMessage(null);
  }, 3000);

  return () => clearTimeout(timer);
}, [successMessage]);

  async function handleCreatePlan(e: React.FormEvent) {
    e.preventDefault();

    const title = planTitle.trim();
    const description = planDescription.trim();

    if (!title) return;

    try {
      const created = await createPlan({
        thread_id: threadId,
        title,
        description: description || undefined,
      });

      setPlans((prev) => [...prev, created]);
      setSelectedPlanId(created.id);
      setPlanTitle("");
      setPlanDescription("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create plan");
    }
  }

  async function handleAIGeneratedPlan(
  aiSteps: { title: string; description: string }[],
  goal: string
) {
  try {
    setError(null);

    console.log("AI steps received:", aiSteps);

    const createdPlan = await createPlan({
      thread_id: threadId,
      title: goal,
      description: "Generated by Sanctuary from your goal.",
    });

    console.log("Created plan:", createdPlan);

    setPlans((prev) => [...prev, createdPlan]);
    setSelectedPlanId(createdPlan.id);

  await Promise.all(
  aiSteps.map((step, index) =>
    createStep(createdPlan.id, {
      title: step.title,
      description: step.description || undefined,
      position: index + 1,
    })
  )
);

onOpenProject(createdPlan);
  } catch (err) {
    console.error("Failed to create guided plan:", err);
    setError(
      err instanceof Error ? err.message : "Failed to create guided plan"
    );
  }
}

  return (
  <section className="thread-workspace">
    <div className="workspace-column">
      
      <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.75rem",
  }}
>
  <h2 style={{ margin: 0 }}>Plans</h2>
  <div style={{ display: "flex", gap: "0.5rem" }}>
  </div>
</div>

<div className="plan-creation-toggle">
  <button
    type="button"
    className={
      creationMode === "guided"
        ? "plan-toggle plan-toggle--active"
        : "plan-toggle"
    }
    onClick={() => setCreationMode("guided")}
  >
    Guide me
  </button>
  <button
    type="button"
    className={
      creationMode === "manual"
        ? "plan-toggle plan-toggle--active"
        : "plan-toggle"
    }
    onClick={() => setCreationMode("manual")}
  >
    I’ll do it myself
  </button>
</div>

{creationMode === "guided" ? (
  <PlanBuilder onPlanCreated={handleAIGeneratedPlan} />
) : null}

{creationMode === "manual" ? (
  <form onSubmit={handleCreatePlan} className="workspace-form">
    <input
      type="text"
      value={planTitle}
      onChange={(e) => setPlanTitle(e.target.value)}
      placeholder="Plan title"
    />
    <textarea
      value={planDescription}
      onChange={(e) => setPlanDescription(e.target.value)}
      placeholder="Plan description"
      rows={3}
    />
    <button type="submit">Create plan</button>
  </form>
) : null}
     </div> 
    <div className="workspace-column">
  <h2>Your projects</h2>

  {loadingPlans ? <p>Loading projects...</p> : null}

  <div className="plan-list">
    {plans
      .filter((plan) => (showArchived ? plan.is_archived : !plan.is_archived))
      .map((plan) => (
        <div key={plan.id} className="plan-card">
          <strong>{plan.title}</strong>
          {plan.description ? <span>{plan.description}</span> : null}

          <button type="button" onClick={() => onOpenProject(plan)}>
            Continue
          </button>
        </div>
      ))}
  </div>

  {successMessage ? <p className="workspace-success">{successMessage}</p> : null}
  {error ? <p className="workspace-error">{error}</p> : null}
</div>
  </section>
);
}